import { updateProgress } from "@/lib/api";
import type { UpdateProgressRequest } from "@/lib/api-types";
import { useMutation } from "@tanstack/react-query";
import { type RefObject, useEffect } from "react";

export function useVideoProgress(moduleId: string, videoRef: RefObject<HTMLVideoElement | null>) {
	const mutation = useMutation({
		mutationFn: (body: UpdateProgressRequest) => updateProgress(moduleId, body),
	});

	const { mutate } = mutation;

	useEffect(() => {
		const video = videoRef.current;
		if (!video || !moduleId) return;

		const sendProgress = () => {
			const position = Math.floor(video.currentTime);
			const duration = Math.floor(video.duration);
			const percent = duration > 0 ? position / duration : 0;

			mutate({
				video_position: position,
				status: percent >= 0.9 ? "completed" : "in_progress",
			});
		};

		const interval = setInterval(() => {
			if (video.paused || video.ended) return;
			sendProgress();
		}, 30_000);

		const onEnded = () => sendProgress();
		video.addEventListener("ended", onEnded);

		return () => {
			clearInterval(interval);
			video.removeEventListener("ended", onEnded);
		};
	}, [moduleId, mutate, videoRef]);
}
